import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../store/authStore';
import { Card, Button } from '../components';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import api from '../services/api';
import toast from 'react-hot-toast';
import { CANONICAL_PROFESSIONAL_TYPES, PROFESSIONAL_TYPE_LABELS, EXPERIENCE_LEVEL_LABELS } from '../types/professionalRates';
import { MIN_HOURLY_RATE } from '../config/rateConstants';

// ─── Constants ─────────────────────────────────────────────────────────────────

const BRANCHES = [
  'Zorg & Welzijn', 'Onderwijs', 'Overheid & Publieke sector', 'Financiële dienstverlening',
  'ICT & Technologie', 'Productie & Industrie', 'Retail & Handel', 'Horeca & Toerisme',
  'Transport & Logistiek', 'Bouw & Vastgoed', 'Juridische dienstverlening',
  'Marketing & Communicatie', 'HR & Organisatieadvies', 'Non-profit & Maatschappelijk', 'Overig',
];

const PROVINCES = [
  'Noord-Holland', 'Zuid-Holland', 'Utrecht', 'Gelderland', 'Noord-Brabant',
  'Limburg', 'Zeeland', 'Friesland', 'Groningen', 'Drenthe', 'Overijssel', 'Flevoland',
];

const LANGUAGES = ['Nederlands', 'Engels', 'Duits', 'Frans', 'Spaans', 'Italiaans', 'Portugees', 'Pools', 'Turks', 'Arabisch'];

const SELECTIEPROCEDURES = [
  { value: 'direct', label: 'Direct beginnen bij akkoord' },
  { value: 'telefonisch', label: 'Eerst telefonisch contact' },
  { value: 'locatie', label: 'Gesprek op locatie' },
  { value: 'teams', label: 'Gesprek via Teams' },
];

const EXPERIENCE_LEVELS = ['0-2', '3-5', '6-10', '11+'] as const;

/** employer_vacancies.min_compliance_tier; de Job-Radar en de vacaturelijst van de professional handhaven hem. */
const MIN_TIERS = [
  { value: '', label: 'Geen eis' },
  { value: 'BRONS', label: 'Minimaal Brons' },
  { value: 'ZILVER', label: 'Minimaal Zilver' },
  { value: 'GOUD', label: 'Uitsluitend Goud' },
] as const;

interface TaalEis { language: string; notes: string; }

interface VacatureForm {
  professional_type: string;
  beschrijving: string;
  vereiste_vaardigheden: string;
  goed_om_te_weten: string;
  // Bedrijf
  bedrijfsnaam: string;
  branche: string;
  adres: string;
  postcode: string;
  plaats: string;
  provincie: string;
  contact_naam: string;
  contact_email: string;
  contact_telefoon: string;
  // Eisen
  experience_level_required: string;
  nobco_required: boolean;
  min_compliance_tier: '' | 'GOUD' | 'ZILVER' | 'BRONS';
  tarief_type: 'in_overleg' | 'vanaf' | 'range';
  tarief_vanaf: string;
  tarief_tot: string;
  uren_per_week_type: 'fulltime' | 'parttime' | 'range' | 'onbekend';
  uren_per_week_min: string;
  uren_per_week_max: string;
  // Procedure
  taaleisen: TaalEis[];
  sluitingsdatum: string;
  selectieprocedure: string;
}

const EMPTY: VacatureForm = {
  professional_type: '', beschrijving: '', vereiste_vaardigheden: '', goed_om_te_weten: '',
  bedrijfsnaam: '', branche: '', adres: '', postcode: '', plaats: '', provincie: '',
  contact_naam: '', contact_email: '', contact_telefoon: '',
  experience_level_required: '', nobco_required: false, min_compliance_tier: '',
  tarief_type: 'in_overleg', tarief_vanaf: '', tarief_tot: '',
  uren_per_week_type: 'onbekend', uren_per_week_min: '', uren_per_week_max: '',
  taaleisen: [{ language: 'Nederlands', notes: '' }],
  sluitingsdatum: '', selectieprocedure: '',
};

// ─── Sub-components (defined outside to avoid remount on parent re-render) ──────

const SectionHeader: React.FC<{ title: string; sub?: string }> = ({ title, sub }) => (
  <div className="mb-4">
    <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
    {sub && <p className="text-sm text-gray-500 mt-0.5">{sub}</p>}
  </div>
);

const Field: React.FC<{ label: string; required?: boolean; hint?: string; children: React.ReactNode }> = ({ label, required, hint, children }) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-1">
      {label}{required && <span className="text-red-500 ml-0.5">*</span>}
    </label>
    {children}
    {hint && <p className="mt-1 text-xs text-gray-400">{hint}</p>}
  </div>
);

// ─── Component ─────────────────────────────────────────────────────────────────

export const OpdrachtCreatePage: React.FC = () => {
  useTranslation('pages');
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const isEditMode = !!id;
  const [form, setForm] = useState<VacatureForm>(EMPTY);
  const [saving, setSaving] = useState(false);
  const [showLangDropdown, setShowLangDropdown] = useState(false);

  // Employer profile for auto-fill
  const { data: employerProfile } = useQuery({
    queryKey: ['employer-profile'],
    queryFn: async () => {
      const res = await api.get('/employer/profile');
      return res.data?.data || res.data;
    },
    staleTime: 5 * 60 * 1000,
  });

  // Auto-fill from profile on load
  useEffect(() => {
    if (!employerProfile) return;
    setForm(prev => ({
      ...prev,
      bedrijfsnaam: prev.bedrijfsnaam || employerProfile.company_name || employerProfile.bedrijfsnaam || '',
      adres: prev.adres || employerProfile.address || employerProfile.straat || '',
      postcode: prev.postcode || employerProfile.postcode || '',
      plaats: prev.plaats || employerProfile.city || employerProfile.plaats || '',
      contact_naam: prev.contact_naam || employerProfile.contact_person_name || employerProfile.name || '',
      contact_email: prev.contact_email || employerProfile.contact_email || user?.email || '',
      contact_telefoon: prev.contact_telefoon || employerProfile.contact_phone || employerProfile.phone || '',
    }));
  }, [employerProfile, user]);

  // Pre-fill professional_type from navigation state (from search page)
  useEffect(() => {
    const state = location.state as any;
    if (state?.criteria?.professional_type) {
      setForm(prev => ({ ...prev, professional_type: state.criteria.professional_type }));
    }
  }, [location.state]);

  // Load existing vacancy in edit mode
  const { data: existingVacancy } = useQuery({
    queryKey: ['vacancy', id],
    queryFn: async () => {
      const res = await api.get(`/employer/vacancy/${id}`);
      return res.data?.data || res.data;
    },
    enabled: isEditMode,
  });

  useEffect(() => {
    if (!existingVacancy) return;
    const cp = existingVacancy.contact_person || {};
    const taaleisen = (() => {
      const raw = existingVacancy.taaleisen;
      if (!raw) return [{ language: 'Nederlands', notes: '' }];
      if (Array.isArray(raw)) return raw;
      try { return JSON.parse(raw); } catch { return [{ language: 'Nederlands', notes: '' }]; }
    })();
    setForm({
      professional_type: existingVacancy.professional_type || '',
      beschrijving: existingVacancy.beschrijving || '',
      vereiste_vaardigheden: existingVacancy.vereiste_vaardigheden || '',
      goed_om_te_weten: existingVacancy.goed_om_te_weten || '',
      bedrijfsnaam: existingVacancy.bedrijfsnaam || '',
      branche: existingVacancy.branche || '',
      adres: existingVacancy.company_location || '',
      postcode: existingVacancy.postcode || '',
      plaats: existingVacancy.plaats || '',
      provincie: existingVacancy.provincie || '',
      contact_naam: cp.naam || '',
      contact_email: cp.email || '',
      contact_telefoon: cp.telefoon || '',
      experience_level_required: existingVacancy.experience_level_required || '',
      nobco_required: existingVacancy.nobco_required || false,
      min_compliance_tier: existingVacancy.min_compliance_tier || '',
      tarief_type: existingVacancy.tarief_type || 'in_overleg',
      tarief_vanaf: existingVacancy.tarief_vanaf ? String(existingVacancy.tarief_vanaf) : '',
      tarief_tot: existingVacancy.tarief_tot ? String(existingVacancy.tarief_tot) : '',
      uren_per_week_type: existingVacancy.uren_per_week_type || 'onbekend',
      uren_per_week_min: existingVacancy.uren_per_week_min ? String(existingVacancy.uren_per_week_min) : '',
      uren_per_week_max: existingVacancy.uren_per_week_max ? String(existingVacancy.uren_per_week_max) : '',
      taaleisen,
      sluitingsdatum: existingVacancy.sluitingsdatum || '',
      selectieprocedure: existingVacancy.selectieprocedure || '',
    });
  }, [existingVacancy]);

  const set = (field: keyof VacatureForm, value: any) =>
    setForm(prev => ({ ...prev, [field]: value }));

  const buildPayload = (status: 'draft' | 'published') => ({
    professional_type: form.professional_type,
    titel: `${PROFESSIONAL_TYPE_LABELS[form.professional_type as keyof typeof PROFESSIONAL_TYPE_LABELS] ?? form.professional_type} — ${form.bedrijfsnaam || 'Opdracht'}`,
    beschrijving: form.beschrijving,
    vereiste_vaardigheden: form.vereiste_vaardigheden,
    goed_om_te_weten: form.goed_om_te_weten || null,
    company_location: [form.adres, form.postcode, form.plaats].filter(Boolean).join(', '),
    provincie: form.provincie,
    branche: form.branche,
    contact_person: {
      naam: form.contact_naam,
      email: form.contact_email,
      telefoon: form.contact_telefoon,
    },
    tarief_type: form.tarief_type,
    tarief_vanaf: form.tarief_vanaf ? parseFloat(form.tarief_vanaf) : null,
    tarief_tot: form.tarief_tot ? parseFloat(form.tarief_tot) : null,
    uren_per_week_type: form.uren_per_week_type,
    uren_per_week_min: form.uren_per_week_min ? parseInt(form.uren_per_week_min) : null,
    uren_per_week_max: form.uren_per_week_max ? parseInt(form.uren_per_week_max) : null,
    experience_level_required: form.experience_level_required || null,
    nobco_required: form.nobco_required,
    min_compliance_tier: form.min_compliance_tier || null,
    taaleisen: form.taaleisen,
    sluitingsdatum: form.sluitingsdatum || null,
    selectieprocedure: form.selectieprocedure,
    status,
  });

  const handleSave = async (status: 'draft' | 'published') => {
    if (!form.professional_type) { toast.error('Selecteer eerst een type professional'); return; }
    if (!form.beschrijving.trim()) { toast.error('Omschrijving is verplicht'); return; }
    setSaving(true);
    try {
      const payload = buildPayload(status);
      if (isEditMode) {
        await api.put(`/employer/vacancy/${id}`, payload);
        if (status === 'published') await api.patch(`/employer/vacancy/${id}/publish`);
        toast.success('Vacature bijgewerkt');
      } else {
        await api.post('/employer/vacancy', payload);
        toast.success(status === 'published' ? 'Vacature geplaatst! Professionals worden genotificeerd.' : 'Vacature opgeslagen als concept');
      }
      navigate('/employer/dashboard/zoeken?route=vacatures');
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Er is een fout opgetreden');
    } finally {
      setSaving(false);
    }
  };

  const addTaal = (lang: string) => {
    if (form.taaleisen.some(t => t.language === lang)) return;
    set('taaleisen', [...form.taaleisen, { language: lang, notes: '' }]);
    setShowLangDropdown(false);
  };

  const removeTaal = (lang: string) => {
    if (lang === 'Nederlands') return;
    set('taaleisen', form.taaleisen.filter(t => t.language !== lang));
  };

  const updateTaalNotes = (lang: string, notes: string) => {
    set('taaleisen', form.taaleisen.map(t => t.language === lang ? { ...t, notes } : t));
  };

  const inputCls = "w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500";

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            {isEditMode ? 'Vacature bewerken' : 'Vacature plaatsen'}
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Vul de opdracht in. Professionals van het gekozen type ontvangen een melding zodra u plaatst.
          </p>
        </div>
        <Button variant="outline" onClick={() => navigate(-1)}>Annuleren</Button>
      </div>

      <div className="space-y-6">

        {/* ── Sectie A: Over de opdracht ──────────────────────────────── */}
        <Card>
          <SectionHeader title="Over de opdracht" />
          <div className="space-y-4">
            <Field label="Type professional" required>
              <select
                className={inputCls}
                value={form.professional_type}
                onChange={e => set('professional_type', e.target.value)}
              >
                <option value="">— Kies een type —</option>
                {CANONICAL_PROFESSIONAL_TYPES.map(type => (
                  <option key={type} value={type}>{PROFESSIONAL_TYPE_LABELS[type]}</option>
                ))}
              </select>
            </Field>

            <Field label="Omschrijving van de opdracht" required>
              <textarea
                className={`${inputCls} min-h-[120px] resize-y`}
                value={form.beschrijving}
                onChange={e => set('beschrijving', e.target.value)}
                placeholder="Beschrijf de opdracht, context, doelstelling en doelgroep..."
              />
            </Field>

            <Field label="Vereiste vaardigheden" hint="Vrij in te vullen, bijv. ervaring met burn-out trajecten, kennis van Wet Poortwachter">
              <textarea
                className={`${inputCls} min-h-[80px] resize-y`}
                value={form.vereiste_vaardigheden}
                onChange={e => set('vereiste_vaardigheden', e.target.value)}
                placeholder="Bijv. aantoonbare ervaring met re-integratie, kennis van cao zorg..."
              />
            </Field>

            <Field label="Goed om te weten" hint="Optioneel — extra informatie voor de professional">
              <textarea
                className={`${inputCls} min-h-[60px] resize-y`}
                value={form.goed_om_te_weten}
                onChange={e => set('goed_om_te_weten', e.target.value)}
                placeholder="Bijv. parkeren aanwezig, hybride werken deels mogelijk, reiskosten vergoed..."
              />
            </Field>
          </div>
        </Card>

        {/* ── Sectie B: Over het bedrijf ──────────────────────────────── */}
        <Card>
          <SectionHeader title="Over het bedrijf" sub="Automatisch ingevuld vanuit uw profiel — aanpasbaar" />
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Bedrijfsnaam / Opdrachtgever" required>
                <input
                  type="text"
                  className={inputCls}
                  value={form.bedrijfsnaam}
                  onChange={e => set('bedrijfsnaam', e.target.value)}
                />
              </Field>
              <Field label="Branche">
                <select className={inputCls} value={form.branche} onChange={e => set('branche', e.target.value)}>
                  <option value="">— Selecteer branche —</option>
                  {BRANCHES.map(b => <option key={b} value={b}>{b}</option>)}
                </select>
              </Field>
            </div>

            <Field label="Adres (locatie opdracht)">
              <input type="text" className={inputCls} value={form.adres} onChange={e => set('adres', e.target.value)} placeholder="Straat + huisnummer" />
            </Field>

            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <Field label="Postcode">
                <input type="text" className={inputCls} value={form.postcode} onChange={e => set('postcode', e.target.value)} placeholder="1234 AB" />
              </Field>
              <Field label="Plaats">
                <input type="text" className={inputCls} value={form.plaats} onChange={e => set('plaats', e.target.value)} />
              </Field>
              <Field label="Provincie">
                <select className={inputCls} value={form.provincie} onChange={e => set('provincie', e.target.value)}>
                  <option value="">— Selecteer —</option>
                  {PROVINCES.map(p => <option key={p} value={p}>{p}</option>)}
                </select>
              </Field>
            </div>

            <div className="pt-2 border-t">
              <p className="text-sm font-medium text-gray-700 mb-3">Contactpersoon</p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Field label="Naam">
                  <input type="text" className={inputCls} value={form.contact_naam} onChange={e => set('contact_naam', e.target.value)} />
                </Field>
                <Field label="E-mailadres">
                  <input type="email" className={inputCls} value={form.contact_email} onChange={e => set('contact_email', e.target.value)} />
                </Field>
                <Field label="Telefoonnummer">
                  <input type="tel" className={inputCls} value={form.contact_telefoon} onChange={e => set('contact_telefoon', e.target.value)} />
                </Field>
              </div>
            </div>
          </div>
        </Card>

        {/* ── Sectie C: Eisen & Voorwaarden ──────────────────────────── */}
        <Card>
          <SectionHeader title="Eisen & Voorwaarden" />
          <div className="space-y-5">

            {/* Ervaringsniveau */}
            <Field label="Gewenst ervaringsniveau ZZP'er">
              <div className="flex flex-wrap gap-2 mt-1">
                {EXPERIENCE_LEVELS.map(lvl => (
                  <label key={lvl} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-sm cursor-pointer transition-colors ${form.experience_level_required === lvl ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-700 border-gray-300 hover:border-blue-400'}`}>
                    <input type="radio" name="exp_level" value={lvl} checked={form.experience_level_required === lvl} onChange={() => set('experience_level_required', lvl)} className="sr-only" />
                    {EXPERIENCE_LEVEL_LABELS[lvl]}
                  </label>
                ))}
                {form.experience_level_required && (
                  <button type="button" onClick={() => set('experience_level_required', '')} className="text-xs text-gray-400 hover:text-red-500 px-2">✕ wissen</button>
                )}
              </div>
              <label className="flex items-center gap-2 mt-2">
                <input type="checkbox" checked={form.nobco_required} onChange={e => set('nobco_required', e.target.checked)} className="rounded" />
                <span className="text-sm text-gray-700">NOBCO/EIA certificering vereist</span>
              </label>
            </Field>

            {/* Minimale vetting-tier: professionals onder deze tier zien de vacature niet */}
            <Field label="Minimale vetting-tier">
              <div className="flex flex-wrap gap-2 mt-1">
                {MIN_TIERS.map(opt => (
                  <label key={opt.value || 'geen'} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-sm cursor-pointer transition-colors ${form.min_compliance_tier === opt.value ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-700 border-gray-300 hover:border-blue-400'}`}>
                    <input type="radio" name="min_tier" value={opt.value} checked={form.min_compliance_tier === opt.value} onChange={() => set('min_compliance_tier', opt.value)} className="sr-only" />
                    {opt.label}
                  </label>
                ))}
              </div>
              <p className="text-xs text-gray-500 mt-1.5">Goud = register-geverifieerd en arbo-vrijgesteld · Zilver = arbo-specialist · Brons = basis-geverifieerd</p>
            </Field>

            {/* Tarief */}
            <Field label="Tarief">
              <div className="space-y-3">
                <div className="flex gap-3 flex-wrap">
                  {(['in_overleg', 'vanaf', 'range'] as const).map(opt => (
                    <label key={opt} className={`flex items-center gap-2 px-3 py-1.5 rounded border text-sm cursor-pointer transition-colors ${form.tarief_type === opt ? 'bg-blue-50 border-blue-500 text-blue-700' : 'border-gray-300 text-gray-700 hover:border-gray-400'}`}>
                      <input type="radio" name="tarief_type" value={opt} checked={form.tarief_type === opt} onChange={() => set('tarief_type', opt)} className="sr-only" />
                      {opt === 'in_overleg' ? 'In overleg' : opt === 'vanaf' ? 'Vanaf €X' : 'Bandbreedte €X – €Y'}
                    </label>
                  ))}
                </div>
                {form.tarief_type === 'vanaf' && (
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-600">Vanaf</span>
                    <input type="number" min={MIN_HOURLY_RATE} className={`${inputCls} w-32`} value={form.tarief_vanaf} onChange={e => set('tarief_vanaf', e.target.value)} placeholder="bijv. 90" />
                    <span className="text-sm text-gray-600">€/uur</span>
                  </div>
                )}
                {form.tarief_type === 'range' && (
                  <div className="flex items-center gap-2">
                    <input type="number" min={MIN_HOURLY_RATE} className={`${inputCls} w-28`} value={form.tarief_vanaf} onChange={e => set('tarief_vanaf', e.target.value)} placeholder="van" />
                    <span className="text-sm text-gray-500">–</span>
                    <input type="number" min={MIN_HOURLY_RATE} className={`${inputCls} w-28`} value={form.tarief_tot} onChange={e => set('tarief_tot', e.target.value)} placeholder="tot" />
                    <span className="text-sm text-gray-600">€/uur</span>
                  </div>
                )}
              </div>
            </Field>

            {/* Uren per week */}
            <Field label="Uren per week">
              <div className="space-y-3">
                <div className="flex flex-wrap gap-3">
                  {([
                    { value: 'fulltime', label: 'Fulltime (~40 u/w)' },
                    { value: 'parttime', label: 'Parttime' },
                    { value: 'range', label: 'Schatting (range)' },
                    { value: 'onbekend', label: 'Niet te voorspellen' },
                  ] as const).map(opt => (
                    <label key={opt.value} className={`flex items-center gap-2 px-3 py-1.5 rounded border text-sm cursor-pointer transition-colors ${form.uren_per_week_type === opt.value ? 'bg-blue-50 border-blue-500 text-blue-700' : 'border-gray-300 text-gray-700 hover:border-gray-400'}`}>
                      <input type="radio" name="uren_type" value={opt.value} checked={form.uren_per_week_type === opt.value} onChange={() => set('uren_per_week_type', opt.value)} className="sr-only" />
                      {opt.label}
                    </label>
                  ))}
                </div>
                {(form.uren_per_week_type === 'parttime' || form.uren_per_week_type === 'range') && (
                  <div className="flex items-center gap-2">
                    <input type="number" min="1" max="40" className={`${inputCls} w-20`} value={form.uren_per_week_min} onChange={e => set('uren_per_week_min', e.target.value)} placeholder="min" />
                    <span className="text-sm text-gray-500">–</span>
                    <input type="number" min="1" max="40" className={`${inputCls} w-20`} value={form.uren_per_week_max} onChange={e => set('uren_per_week_max', e.target.value)} placeholder="max" />
                    <span className="text-sm text-gray-600">uur/week</span>
                  </div>
                )}
              </div>
            </Field>
          </div>
        </Card>

        {/* ── Sectie D: Procedure ────────────────────────────────────── */}
        <Card>
          <SectionHeader title="Procedure" />
          <div className="space-y-5">

            {/* Taaleisen */}
            <Field label="Taaleisen">
              <div className="space-y-2 mt-1">
                {form.taaleisen.map(te => (
                  <div key={te.language} className="flex items-start gap-2 p-2 bg-gray-50 rounded border">
                    <span className="text-sm font-medium text-gray-800 w-28 shrink-0 pt-1">{te.language}</span>
                    <input
                      type="text"
                      className="flex-1 px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                      value={te.notes}
                      onChange={e => updateTaalNotes(te.language, e.target.value)}
                      placeholder="Optioneel: toelichting (bijv. vloeiend B2)"
                    />
                    {te.language !== 'Nederlands' && (
                      <button type="button" onClick={() => removeTaal(te.language)} className="text-gray-400 hover:text-red-500 text-lg leading-none pt-1">×</button>
                    )}
                  </div>
                ))}
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => setShowLangDropdown(!showLangDropdown)}
                    className="text-sm text-blue-600 hover:text-blue-700 border border-blue-300 rounded px-2 py-1 hover:bg-blue-50"
                  >
                    + Taal toevoegen
                  </button>
                  {showLangDropdown && (
                    <div className="absolute z-10 mt-1 w-40 bg-white border border-gray-200 rounded-lg shadow-lg">
                      {LANGUAGES.filter(l => !form.taaleisen.some(t => t.language === l)).map(lang => (
                        <button key={lang} type="button" onClick={() => addTaal(lang)} className="w-full text-left px-3 py-2 text-sm hover:bg-blue-50">
                          {lang}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </Field>

            {/* Sluitingsdatum */}
            <Field label="Sluitingsdatum" hint="Reacties ontvangen tot en met deze datum">
              <input
                type="date"
                className={`${inputCls} w-48`}
                value={form.sluitingsdatum}
                onChange={e => set('sluitingsdatum', e.target.value)}
                min={new Date().toISOString().split('T')[0]}
              />
            </Field>

            {/* Selectieprocedure */}
            <Field label="Selectieprocedure">
              <div className="space-y-2 mt-1">
                {SELECTIEPROCEDURES.map(opt => (
                  <label key={opt.value} className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${form.selectieprocedure === opt.value ? 'bg-blue-50 border-blue-400' : 'border-gray-200 hover:border-gray-400'}`}>
                    <input
                      type="radio"
                      name="selectieprocedure"
                      value={opt.value}
                      checked={form.selectieprocedure === opt.value}
                      onChange={() => set('selectieprocedure', opt.value)}
                      className="text-blue-600"
                    />
                    <span className="text-sm text-gray-700">{opt.label}</span>
                  </label>
                ))}
              </div>
            </Field>
          </div>
        </Card>

        {/* ── Acties ──────────────────────────────────────────────────── */}
        <div className="flex justify-between items-center pt-2">
          <Button variant="outline" onClick={() => navigate(-1)}>Annuleren</Button>
          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={() => handleSave('draft')}
              disabled={saving}
            >
              Opslaan als concept
            </Button>
            <Button
              onClick={() => handleSave('published')}
              disabled={saving}
            >
              {saving ? 'Bezig...' : 'Vacature plaatsen'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
